import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { Card } from "../../components/UI";
import {
  HandCoins, LayoutTemplate, CalendarDays, Globe, Megaphone, Sparkles,
  MessageSquare, ArrowRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { icon: LayoutTemplate, label: "Donation Page", desc: "A branded page that accepts contributions in minutes", to: "/fundraising/landing-pages", testid: "fundraise-card-donation-page" },
  { icon: HandCoins, label: "Fundraising Campaign", desc: "Set a goal, track progress, rally your supporters", to: "/fundraising/campaigns", testid: "fundraise-card-campaign" },
  { icon: CalendarDays, label: "Fundraising Event", desc: "Create an event with public RSVP and volunteer shifts", to: "/events", testid: "fundraise-card-event" },
  { icon: Globe, label: "Campaign Microsite", desc: "A shareable mini-site for your fundraiser", to: "/fundraising/microsites", testid: "fundraise-card-microsite" },
  { icon: Megaphone, label: "Promote Existing Fundraiser", desc: "Turn a fundraiser into an ad campaign with one click", to: "/fundraising/campaigns", testid: "fundraise-card-promote" },
  { icon: MessageSquare, label: "Text-to-Give", desc: "Supporters text a keyword, get a donation link back", to: "/fundraising/sms", testid: "fundraise-card-sms" },
];

export default function SimpleFundraise() {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  const askAI = async () => {
    setStarting(true);
    try {
      const r = await api.post("/agent/sessions", { template: null });
      navigate(`/campaign-agent/${r.data.id}`, {
        state: { prefill: "Help me build a fundraising campaign — create the fundraiser, landing page and promotion plan." },
      });
    } catch (e) {
      toast.error(errMsg(e, "Could not start the AI builder"));
      setStarting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="simple-fundraise">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Fundraise</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">What would you like to create?</h1>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Every option below is compliance-ready — contribution limits, attestations and receipts are handled for you.
      </p>

      {/* AI-first card */}
      <Card className="mb-6 border-cyan-400/25 bg-gradient-to-r from-cyan-400/[0.07] to-transparent" data-testid="fundraise-ai-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/15 border border-cyan-400/25 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold">Ask AI to build it</h3>
              <p className="text-xs text-slate-400 mt-1">Tell TotalReach your goal — e.g. "Raise $100,000 over four weeks" — and it drafts the fundraiser, page and promotion plan for your approval.</p>
            </div>
          </div>
          <button onClick={askAI} disabled={starting} data-testid="fundraise-ask-ai-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Ask AI to build it
          </button>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="fundraise-options">
        {OPTIONS.map((o) => (
          <button key={o.testid} onClick={() => navigate(o.to)} data-testid={o.testid}
            className="group text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 hover:bg-cyan-400/[0.04] transition-colors p-5">
            <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mb-3">
              <o.icon className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="font-display text-base font-bold">{o.label}</div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{o.desc}</p>
          </button>
        ))}
      </div>

      <div className="text-center">
        <button onClick={() => navigate("/fundraising")} data-testid="fundraise-open-full-module"
          className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5 transition-colors">
          Open Full Fundraising Center <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
